/**
 * Keep Google Maps SDK for map display, but tightly throttle Directions API
 * so we stay inside the free monthly credit.
 *
 * Directions is the SKU that burned the old go7-logistics bill — never poll it.
 */

import { distanceMeters, type LatLng } from './locationSmoothing';

const DAY_KEY = 'go7-directions-day';
const COUNT_KEY = 'go7-directions-count';
const CACHE_KEY = 'go7-directions-cache-v1';

/** Hard daily cap per device — raise only after billing is healthy */
export const DIRECTIONS_DAILY_BUDGET = 60;

/** Nav: only re-call Directions after this much movement */
export const NAV_REFETCH_MOVE_METERS = 400;

/** Nav: never re-call Directions more often than this */
export const NAV_REFETCH_MIN_MS = 90_000;

/** Overview (pickup→delivery): treat origins within this as the same trip */
export const OVERVIEW_CACHE_ROUND = 3; // ~110m precision

type CachedRoute = {
  path: LatLng[];
  distanceMeters: number;
  durationMinutes: number;
  distanceLabel: string;
  etaLabel: string;
  savedAt: number;
};

type RouteResult = CachedRoute | null;

function todayStamp(): string {
  return new Date().toISOString().slice(0, 10);
}

function readDayCount(): number {
  try {
    if (localStorage.getItem(DAY_KEY) !== todayStamp()) {
      localStorage.setItem(DAY_KEY, todayStamp());
      localStorage.setItem(COUNT_KEY, '0');
      return 0;
    }
    return Number(localStorage.getItem(COUNT_KEY) || '0') || 0;
  } catch {
    return 0;
  }
}

function bumpDayCount(): void {
  try {
    const next = readDayCount() + 1;
    localStorage.setItem(DAY_KEY, todayStamp());
    localStorage.setItem(COUNT_KEY, String(next));
  } catch {
    /* ignore quota errors */
  }
}

export function getDirectionsUsageToday(): { used: number; budget: number; remaining: number } {
  const used = readDayCount();
  return {
    used,
    budget: DIRECTIONS_DAILY_BUDGET,
    remaining: Math.max(0, DIRECTIONS_DAILY_BUDGET - used),
  };
}

export function canSpendDirectionsRequest(): boolean {
  return readDayCount() < DIRECTIONS_DAILY_BUDGET;
}

function routeCacheKey(origin: LatLng, destination: LatLng, precision = OVERVIEW_CACHE_ROUND): string {
  return [
    origin.lat.toFixed(precision),
    origin.lng.toFixed(precision),
    destination.lat.toFixed(precision),
    destination.lng.toFixed(precision),
  ].join('|');
}

function readCache(): Record<string, CachedRoute> {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, CachedRoute>;
  } catch {
    return {};
  }
}

function writeCache(cache: Record<string, CachedRoute>): void {
  try {
    // Cap entries so sessionStorage doesn't grow forever
    const entries = Object.entries(cache);
    const trimmed =
      entries.length > 40
        ? Object.fromEntries(entries.sort((a, b) => b[1].savedAt - a[1].savedAt).slice(0, 40))
        : cache;
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(trimmed));
  } catch {
    /* ignore */
  }
}

export function getCachedDirections(origin: LatLng, destination: LatLng): CachedRoute | null {
  const key = routeCacheKey(origin, destination);
  const hit = readCache()[key];
  if (!hit?.path?.length) return null;
  // Cache valid for 45 minutes
  if (Date.now() - hit.savedAt > 45 * 60 * 1000) return null;
  return hit;
}

function saveCachedDirections(origin: LatLng, destination: LatLng, route: CachedRoute): void {
  const cache = readCache();
  cache[routeCacheKey(origin, destination)] = { ...route, savedAt: Date.now() };
  writeCache(cache);
}

export function shouldRefetchNavRoute(opts: {
  lastOrigin: LatLng | null;
  nextOrigin: LatLng;
  lastFetchedAt: number;
  hasPath: boolean;
  destinationChanged: boolean;
}): boolean {
  if (opts.destinationChanged) return true;
  if (!opts.hasPath || !opts.lastOrigin) return true;

  const moved = distanceMeters(opts.lastOrigin, opts.nextOrigin);
  if (moved < NAV_REFETCH_MOVE_METERS) return false;
  if (Date.now() - opts.lastFetchedAt < NAV_REFETCH_MIN_MS) return false;
  return true;
}

async function fetchMapboxRoadRoute(
  origin: LatLng,
  destination: LatLng
): Promise<CachedRoute | null> {
  try {
    const { fetchInAppRoute } = await import('./orsRouting');
    const route = await fetchInAppRoute(origin, destination, { force: true });
    if (!route?.path?.length || route.source === 'straight') return null;
    return {
      path: route.path,
      distanceMeters: route.distanceMeters,
      durationMinutes: route.durationMinutes,
      distanceLabel: route.distanceLabel,
      etaLabel: route.etaLabel,
      savedAt: Date.now(),
    };
  } catch (e) {
    console.warn('[Directions] Mapbox fallback failed:', e);
    return null;
  }
}

/**
 * Prefer Google Directions (go7 RiderJobMap). If denied/fails, use Mapbox road route
 * so the rider still gets street navigation on the map.
 */
export async function fetchGoogleDirections(opts: {
  origin: LatLng;
  destination: LatLng;
  force?: boolean;
}): Promise<RouteResult> {
  const { origin, destination, force = false } = opts;

  const cached = getCachedDirections(origin, destination);
  if (cached && !force) {
    return cached;
  }

  if (!canSpendDirectionsRequest()) {
    console.warn(
      '[Directions] Daily budget reached — trying Mapbox / cache.',
      getDirectionsUsageToday()
    );
    return (await fetchMapboxRoadRoute(origin, destination)) || cached;
  }

  const google = (window as any).google;
  if (!google?.maps?.DirectionsService) {
    return (await fetchMapboxRoadRoute(origin, destination)) || cached;
  }

  bumpDayCount();

  const googleRoute = await new Promise<CachedRoute | null>((resolve) => {
    const service = new google.maps.DirectionsService();
    service.route(
      {
        origin,
        destination,
        travelMode: google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: false,
      },
      (result: any, status: string) => {
        if (status !== 'OK' || !result?.routes?.[0]) {
          console.warn('[Directions] Google failed:', status);
          resolve(null);
          return;
        }

        const leg = result.routes[0].legs[0];
        const path: LatLng[] = [];
        result.routes[0].overview_path?.forEach((p: any) => {
          path.push({ lat: p.lat(), lng: p.lng() });
        });

        resolve({
          path,
          distanceMeters: leg?.distance?.value ?? 0,
          durationMinutes: (leg?.duration?.value ?? 0) / 60,
          distanceLabel:
            leg?.distance?.text || `${Math.round((leg?.distance?.value ?? 0) / 1000)} km`,
          etaLabel:
            leg?.duration?.text || `${Math.round((leg?.duration?.value ?? 0) / 60)} mins`,
          savedAt: Date.now(),
        });
      }
    );
  });

  if (googleRoute?.path?.length) {
    saveCachedDirections(origin, destination, googleRoute);
    return googleRoute;
  }

  const mapbox = await fetchMapboxRoadRoute(origin, destination);
  if (mapbox) {
    saveCachedDirections(origin, destination, mapbox);
    return mapbox;
  }

  return cached;
}
